import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Icon1 from "@assets/icons/Icon";
import { useAtelierStore } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import { useAuthStore } from "@store/auth";
import { ordresApi } from "@services/api/ordres";
import type { Ordre } from "types/ordre";
import VehicleImage from "@components/ui/VehicleImage";
import { MobileTabBar } from "./MobileTabBar";

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatStart(startHour: number): string {
  const h = Math.floor(startHour);
  const m = Math.round((startHour - h) * 60);
  return `${h}h${m > 0 ? String(m).padStart(2, "0") : ""}`;
}

function formatDuration(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h${String(m).padStart(2, "0")}`;
}

export default function MobilePlanningScreen() {
  const navigate = useNavigate();
  const orgData = useDataStore((s) => s.orgData);
  const userData = useDataStore((s) => s.userData);
  const logout = useAuthStore((s) => s.logout);
  const fetchData = useAtelierStore((s) => s.fetchData);
  const jobs = useAtelierStore((s) => s.jobs);

  const [ordreMap, setOrdreMap] = useState<Record<string, Ordre>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orgData?.id) fetchData(orgData.id);
  }, [orgData?.id]);

  const today = todayIso();
  const myJobsToday = jobs
    .filter((j) => j.workerId === userData?.id && j.date === today)
    .sort((a, b) => a.startHour - b.startHour);

  const ordreIds = Array.from(
    new Set(myJobsToday.map((j) => j.ordre_id).filter((id): id is string => !!id)),
  );

  const loadOrdres = useCallback(async () => {
    if (!ordreIds.length) {
      setOrdreMap({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const results = await ordresApi.batch(ordreIds);
      const map: Record<string, Ordre> = {};
      for (const o of results) map[o.id] = o;
      setOrdreMap(map);
    } catch {
      setOrdreMap({});
    }
    setLoading(false);
  }, [ordreIds.join(",")]);

  useEffect(() => {
    loadOrdres();
  }, [loadOrdres]);

  const dateHeading = new Date(today + "T00:00:00").toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div className="flex flex-col bg-white" style={{ fontFamily: "Poppins", height: "100dvh" }}>
      {/* Header */}
      <div className="flex flex-row items-center justify-between" style={{ padding: "max(30px, env(safe-area-inset-top, 30px)) 20px 0" }}>
        <span style={{ fontWeight: 500, fontSize: 20, lineHeight: "30px", color: "#000000" }}>
          Planning du jour
        </span>
        <button onClick={logout} className="flex items-center justify-center" style={{ padding: "0 10px" }}>
          <Icon1 name="logout" size={20} color="#A1A1A1" />
        </button>
      </div>

      {/* Tab bar */}
      <div style={{ padding: "16px 20px 0" }}>
        <MobileTabBar active="planning" />
      </div>

      {/* Date heading */}
      <div style={{ padding: "16px 20px 5px" }}>
        <span style={{ fontWeight: 500, fontSize: 13, lineHeight: "20px", color: "#A8A8A8", textTransform: "capitalize" }}>
          {dateHeading}
        </span>
      </div>

      {/* Jobs list */}
      <div className="flex-1 overflow-y-auto" style={{ padding: "0 20px 20px" }}>
        {loading ? (
          <div className="flex flex-col" style={{ gap: 7 }}>
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse" style={{ height: 88, background: "#F2F2F2", borderRadius: 10 }} />
            ))}
          </div>
        ) : myJobsToday.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16" style={{ gap: 12 }}>
            <div className="flex items-center justify-center" style={{ width: 56, height: 56, borderRadius: 16, background: "#F4F4F4" }}>
              <Icon1 name="calendar" size={28} color="#C4C4C4" />
            </div>
            <div className="flex flex-col items-center" style={{ gap: 4 }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: "#1E1E1E" }}>
                Aucun rendez-vous aujourd'hui
              </span>
              <span style={{ fontWeight: 400, fontSize: 12, color: "#A8A8A8", textAlign: "center", maxWidth: 220 }}>
                Vos interventions de la journée apparaîtront ici
              </span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col" style={{ gap: 7 }}>
            {myJobsToday.map((job) => {
              const ordre = job.ordre_id ? ordreMap[job.ordre_id] : undefined;
              const vehicle = ordre?.vehicle;
              const endHour = job.startHour + job.durationHours;
              return (
                <button
                  key={job.id}
                  disabled={!job.ordre_id}
                  onClick={() => job.ordre_id && navigate(`/m/ordre/${job.ordre_id}`)}
                  className="flex flex-row items-stretch text-left"
                  style={{
                    padding: 4, gap: 10,
                    background: "#FFFFFF",
                    boxShadow: "0px 2px 6.3px rgba(0, 0, 0, 0.15)",
                    borderRadius: 10,
                    opacity: job.ordre_id ? 1 : 0.6,
                  }}
                >
                  {/* Time block */}
                  <div
                    className="flex flex-col items-center justify-center flex-shrink-0"
                    style={{ width: 64, background: "#E8EAFF", borderRadius: 8, padding: "6px 4px" }}
                  >
                    <span style={{ fontWeight: 600, fontSize: 13, color: "#5C6BFF", lineHeight: "18px" }}>
                      {formatStart(job.startHour)}
                    </span>
                    <span style={{ fontWeight: 500, fontSize: 9, color: "#A1A1A1", lineHeight: "12px" }}>
                      → {formatStart(endHour)}
                    </span>
                    <span style={{ fontWeight: 500, fontSize: 9, color: "#A1A1A1", lineHeight: "12px", marginTop: 2 }}>
                      {formatDuration(job.durationHours)}
                    </span>
                  </div>

                  {/* Vehicle thumbnail */}
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ width: 56, background: "#F2F2F2", borderRadius: 8, overflow: "hidden" }}
                  >
                    {vehicle ? (
                      <VehicleImage marque={vehicle.marque} modele={vehicle.modele} coloris={vehicle.coloris} mec={vehicle.mec} className="w-[48px]" iconSize={20} />
                    ) : (
                      <Icon1 name="car" size={20} color="#A1A1A1" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex flex-col justify-center flex-1" style={{ padding: "4px 0", gap: 3, minWidth: 0 }}>
                    <span className="truncate" style={{ fontWeight: 500, fontSize: 12, lineHeight: "18px", color: "#1E1E1E" }}>
                      {ordre?.name || job.clientName || "—"}
                    </span>
                    <span className="truncate" style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A8A8A8" }}>
                      {ordre?.immatriculation || job.plate || "—"}
                    </span>
                    {job.interventions && job.interventions.length > 0 && (
                      <span className="truncate" style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#6366f1" }}>
                        {job.interventions.join(" · ")}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom indicator */}
      <div className="flex justify-center" style={{ padding: "10px 0 max(8px, env(safe-area-inset-bottom, 8px))" }}>
        <div style={{ width: 104, height: 5, background: "#000000", borderRadius: 30 }} />
      </div>
    </div>
  );
}
